import { useEffect } from 'react';

interface OrganizationData {
  type: 'Organization';
  name: string;
  url: string;
  logo: string;
  description: string;
  address: {
    addressCountry: string;
    addressLocality: string;
  };
  geo?: {
    latitude: string;
    longitude: string;
  };
  sameAs?: string[];
}

interface WebSiteData {
  type: 'WebSite';
  name: string;
  url: string;
  potentialAction?: {
    type: string;
    target: string;
    queryInput: string;
  };
}

interface PersonData {
  type: 'Person';
  name: string;
  jobTitle?: string;
  affiliation?: string;
  image?: string;
  url?: string;
}

interface EventData {
  type: 'Event';
  name: string;
  startDate: string;
  location: {
    type: string;
    name: string;
    address: {
      addressCountry: string;
      addressLocality: string;
    };
  };
  description: string;
  organizer: {
    type: string;
    name: string;
  };
}

interface BreadcrumbData {
  type: 'BreadcrumbList';
  itemListElement: Array<{
    position: number;
    name: string;
    item: string;
  }>;
}

type StructuredDataProps = 
  | { data: OrganizationData }
  | { data: WebSiteData }
  | { data: PersonData }
  | { data: EventData }
  | { data: BreadcrumbData };

export const StructuredData = ({ data }: StructuredDataProps) => {
  useEffect(() => {
    const scriptId = `structured-data-${data.type}`;
    let script = document.getElementById(scriptId) as HTMLScriptElement;

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }

    const structuredData = buildStructuredData(data);
    script.textContent = JSON.stringify(structuredData);

    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [data]);

  return null;
};

function buildStructuredData(data: any) {
  const baseUrl = 'https://firsttuesday.dz';

  switch (data.type) {
    case 'Organization':
      return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: data.name,
        url: data.url,
        logo: `${baseUrl}${data.logo}`,
        description: data.description,
        foundingDate: '2024',
        address: {
          '@type': 'PostalAddress',
          addressCountry: data.address.addressCountry,
          addressLocality: data.address.addressLocality
        },
        ...(data.geo && {
          geo: {
            '@type': 'GeoCoordinates',
            latitude: data.geo.latitude,
            longitude: data.geo.longitude
          }
        }),
        ...(data.sameAs && { sameAs: data.sameAs })
      };

    case 'WebSite':
      return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: data.name,
        url: data.url,
        ...(data.potentialAction && {
          potentialAction: {
            '@type': data.potentialAction.type,
            target: data.potentialAction.target,
            'query-input': data.potentialAction.queryInput
          }
        })
      };

    case 'Person':
      return {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: data.name,
        ...(data.jobTitle && { jobTitle: data.jobTitle }),
        ...(data.affiliation && { affiliation: data.affiliation }),
        ...(data.image && { image: data.image }),
        ...(data.url && { url: data.url })
      };

    case 'Event':
      return {
        '@context': 'https://schema.org',
        '@type': 'Event',
        name: data.name,
        startDate: data.startDate,
        location: {
          '@type': data.location.type,
          name: data.location.name,
          address: {
            '@type': 'PostalAddress',
            addressCountry: data.location.address.addressCountry,
            addressLocality: data.location.address.addressLocality
          }
        },
        description: data.description,
        organizer: {
          '@type': data.organizer.type,
          name: data.organizer.name
        }
      };

    case 'BreadcrumbList':
      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: data.itemListElement.map((item: any) => ({
          '@type': 'ListItem',
          position: item.position,
          name: item.name,
          item: `${baseUrl}${item.item}`
        }))
      };

    default:
      return data;
  }
}
